// The orders a house has given its heroes (`heroes.quests`), read off the
// snapshot (the player global `heroQuests`).
//
// The client keeps no copy of a quest: an order that is completed, failed or
// replaced simply changes in the next snapshot. What the client does decide
// is what to *ask* for — which kind of order, and which hero to hunt.
//
// A quest is a contract, not a journey: the hero fights because the house
// told them to, and the sim pays out when the objective is met. See
// `docs/hero-quests-design.md`.

import type { GameSnapshot, SceneNode } from '@aod/protocol';

/** What a hero has been sent to do. */
export type HeroQuestKind = 'slay' | 'hunt';

/** Where a quest stands. */
export type HeroQuestState = 'active' | 'completed' | 'failed';

/** One order, on one hero. */
export interface HeroQuest {
  /** The hero's entity id — the same number the unit node id carries. */
  readonly id: number;
  readonly name: string;
  readonly kind: HeroQuestKind;
  /** The hero to destroy, for a `hunt`; null for a `slay`. */
  readonly target: number | null;
  /** How many kills the order needs. A hunt needs one. */
  readonly amount: number;
  /** How many kills the hero has made. */
  readonly progress: number;
  /** The tick the order was given. */
  readonly started: number;
  /** The tick the order runs out. */
  readonly deadline: number;
  readonly state: HeroQuestState;
}

/** The orders of one house. */
export interface HeroQuests {
  readonly quests: readonly HeroQuest[];
}

/** A hero of another house that is on the board — a hunt may name them. */
export interface HuntTarget {
  /** The node id the sim knows the hero by (`unit_<entity id>`). */
  readonly unitId: string;
  readonly name: string;
}

interface RawPlayer {
  readonly playerIndex: number;
  readonly heroQuests?: { readonly quests?: ReadonlyArray<Partial<HeroQuest>> };
}

/** The orders of `playerIndex`, or null while the module is off. */
export function readHeroQuests(
  snapshot: GameSnapshot | null,
  playerIndex: number,
): HeroQuests | null {
  const globals = snapshot?.state.globals as { players?: ReadonlyArray<RawPlayer> } | undefined;
  const mine = globals?.players?.find((p) => p.playerIndex === playerIndex)?.heroQuests;
  if (!mine) return null;
  return { quests: (mine.quests ?? []).map(normalise) };
}

/** A snapshot is data, not a promise: read every field defensively. */
function normalise(raw: Partial<HeroQuest>): HeroQuest {
  return {
    id: raw.id ?? 0,
    name: raw.name ?? '',
    kind: raw.kind === 'hunt' ? 'hunt' : 'slay',
    target: typeof raw.target === 'number' ? raw.target : null,
    amount: raw.amount ?? 1,
    progress: raw.progress ?? 0,
    started: raw.started ?? 0,
    deadline: raw.deadline ?? 0,
    state: raw.state === 'completed' ? 'completed' : raw.state === 'failed' ? 'failed' : 'active',
  };
}

/** The node id the sim knows this hero by — `unit_<entity id>`. */
export function heroUnitId(quest: HeroQuest): string {
  return `unit_${quest.id}`;
}

/** The entity id a unit node id carries, or null when it is not one. */
export function heroIdOfUnit(unitId: string): number | null {
  const raw = unitId.startsWith('unit_') ? unitId.slice('unit_'.length) : '';
  const n = Number(raw);
  return raw !== '' && Number.isInteger(n) ? n : null;
}

/** The orders still open. */
export function activeQuests(quests: HeroQuests | null): readonly HeroQuest[] {
  return (quests?.quests ?? []).filter((q) => q.state === 'active');
}

/** The order on a given hero, if there is one. */
export function questOfHero(quests: HeroQuests | null, heroId: number): HeroQuest | null {
  return (quests?.quests ?? []).find((q) => q.id === heroId) ?? null;
}

/**
 * The enemy heroes the client can see.
 *
 * A captive is off the board, so a hero in chains is never offered as a
 * target — the sim would refuse the order anyway.
 */
export function huntTargets(
  nodes: readonly SceneNode[],
  localPlayerIndex: number,
): readonly HuntTarget[] {
  return nodes
    .filter((n) => n.type === 'unit')
    .filter((n) => (n.props?.ownerId as number | undefined) !== localPlayerIndex)
    .filter((n) => n.props?.hero !== undefined)
    .map((n) => ({
      unitId: n.id,
      name:
        ((n.props?.hero as Record<string, unknown> | undefined)?.name as string | undefined) ??
        n.id,
    }));
}
